// src/TicketDetails.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaTicketAlt, FaUser, FaCalendarAlt, FaFlag, FaLayerGroup, FaInfoCircle, FaCog } from 'react-icons/fa';

const TicketDetails = ({ ticket, onClose }) => {
  const navigate = useNavigate();

  if (!ticket) return null;

  const getStatusColor = (status) => {
    if (!status) return { bg: '#f1f5f9', text: '#64748b' };
    const s = status.toLowerCase();
    if (s.includes('in progress')) return { bg: '#eff6ff', text: '#2563eb' };
    if (s.includes('on hold') || s.includes('pending')) return { bg: '#fef3c7', text: '#d97706' };
    if (s.includes('resolved') || s.includes('closed')) return { bg: '#ecfdf5', text: '#059669' };
    if (s.includes('new') || s.includes('open')) return { bg: '#fef2f2', text: '#dc2626' };
    return { bg: '#f1f5f9', text: '#64748b' };
  };

  const statusColor = getStatusColor(ticket.status);

  const handleClose = (e) => {
    e.stopPropagation();
    onClose();
  };

  const handleProcessTicket = (e) => {
    e.stopPropagation();
    onClose();
    navigate(`/process/${ticket.ticketId}`);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.5)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
        animation: 'fadeIn 0.2s ease'
      }}
      onClick={handleBackdropClick}
    >
      <div 
        style={{
          background: 'white',
          borderRadius: '20px',
          maxWidth: '560px',
          width: '92%',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.18)',
          animation: 'modalSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #f1f5f9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#fafbfc',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #7B3FF2 0%, #5A2FC2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '14px'
            }}>
              <FaTicketAlt />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>
                {ticket.ticketId}
              </h2>
              <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>Ticket Details</p>
            </div>
          </div>
          <button 
            type="button"
            onClick={handleClose}
            style={{
              background: '#f1f5f9',
              border: 'none',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              fontSize: '14px',
              transition: 'all 0.15s ease',
              padding: 0
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#1e293b'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Scrollable Body */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <div style={{ padding: '24px' }}>
            {/* Status badge */}
            <div style={{ marginBottom: '20px' }}>
              <span style={{
                background: statusColor.bg,
                color: statusColor.text,
                padding: '5px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.3px',
                textTransform: 'capitalize'
              }}>
                {ticket.status}
              </span>
            </div>

            {/* Description */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#1e293b', margin: '0 0 6px 0' }}>
                Description
              </h3>
              <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6, margin: 0 }}>
                {ticket.shortDescription || 'No description available'}
              </p>
            </div>

            {/* Info Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px'
            }}>
              {[
                { icon: <FaLayerGroup />, label: 'Assignment Group', value: ticket.assignmentGroup },
                { icon: <FaUser />, label: 'Assigned To', value: ticket.assignedTo || 'Unassigned' },
                { icon: <FaCalendarAlt />, label: 'Received On', value: ticket.receivedOn },
                { icon: <FaFlag />, label: 'Priority', value: ticket.priority || 'N/A' },
                { icon: <FaInfoCircle />, label: 'Item', value: ticket.item || 'N/A' },
                { icon: <FaCog />, label: 'Category', value: ticket.category || 'N/A' },
              ].map((item, idx) => (
                <div key={idx} style={{
                  padding: '12px',
                  background: '#fafbfc',
                  borderRadius: '10px',
                  border: '1px solid #f1f5f9'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '4px',
                    color: '#94a3b8',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {item.icon}
                    {item.label}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#334155',
                    wordBreak: 'break-word'
                  }}>
                    {item.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Action buttons */}
            <div style={{ marginTop: '24px', display: 'flex', gap: '10px' }}>
              <button 
                type="button"
                onClick={handleProcessTicket}
                style={{
                  flex: 1,
                  padding: '11px 20px',
                  background: 'linear-gradient(135deg, #7B3FF2 0%, #5A2FC2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  letterSpacing: '0.3px',
                  boxShadow: '0 2px 8px rgba(123, 63, 242, 0.25)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(123, 63, 242, 0.35)'; }}
                onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(123, 63, 242, 0.25)'; }}
              >
                PROCESS TICKET
              </button>
              <button 
                type="button"
                onClick={handleClose}
                style={{
                  padding: '11px 20px',
                  background: 'white',
                  color: '#64748b',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#334155'; }}
                onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetails;
